import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { performLogin } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { HttpError } from '@/lib/api';

/**
 * Page Login (Phase 2 — auth réelle).
 * Référence visuelle : STITCH/ktm_maroc_connexion_atelier/ + charte HTML.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isHydrating = useAuthStore((s) => s.isHydrating);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (isHydrating) {
    return (
      <div className="flex h-screen items-center justify-center text-text-muted text-sm">
        Chargement…
      </div>
    );
  }

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await performLogin(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.payload.message);
      } else {
        setError('Erreur de connexion — réessayez');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-bg px-4">
      <div className="w-full max-w-[400px] bg-surface-white rounded-lg shadow-md border border-border-light">
        <div className="px-8 pt-10 pb-8">
          <header className="text-center mb-8">
            <h1 className="text-xl font-bold tracking-tight text-ktm-orange">KTM MAROC</h1>
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted mt-1">
              Atelier Casablanca
            </p>
          </header>

          {error && (
            <div
              role="alert"
              className="mb-6 px-4 py-3 rounded-md bg-status-annule-bg border border-status-annule-border text-status-annule-text text-sm font-medium"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1">
              <label htmlFor="email" className="input-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="username"
                autoFocus
                disabled={loading}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom@ktm-maroc.ma"
                className="input"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="input-label">
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                disabled={loading}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input"
              />
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary w-full">
              {loading ? 'Connexion…' : 'Se connecter'}
              {!loading && <span aria-hidden="true">→</span>}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button type="button" className="btn-tertiary text-sm" disabled>
              Problème de connexion ?
            </button>
          </div>
        </div>

        <footer className="px-8 py-4 border-t border-border-light text-center text-xs text-text-muted font-mono">
          KTM Atelier Board · v0.1.0
        </footer>
      </div>
    </div>
  );
}
